/**
 * InfoContent - Static contextual help content for each page.
 *
 * Used by InfoSidebar to display page-specific guidance.
 */

import type { InfoSection } from '../components/InfoSidebar';

interface PageInfo {
  title: string;
  description?: string;
  sections: InfoSection[];
}

export const INFO_CONTENT: Record<string, PageInfo> = {
  home: {
    title: 'About Subscrypts',
    description: 'Learn about the Subscrypts Telegram Bot.',
    sections: [
      {
        heading: 'What is Subscrypts?',
        body: 'Subscrypts is a blockchain-powered subscription service built on Arbitrum. Telegram group owners (merchants) create subscription plans, and members subscribe using their Ethereum wallets to gain access to exclusive groups.',
      },
      {
        heading: 'How It Works',
        body: 'Merchants create subscription plans on-chain with custom pricing, duration, and currency (SUBS or USDC). Subscribers connect their wallet, pay the subscription fee, and the Telegram bot automatically grants group access. Access is reconciled periodically to ensure only active subscribers retain it.',
      },
      {
        heading: 'Getting Started',
        body: 'To set up Subscrypts for your Telegram group: 1) Add the bot to your group. 2) Follow the setup instructions sent by the bot. 3) Create a subscription plan via the web interface. 4) Share the subscribe link with your members. Members can then subscribe through the generated link.',
      },
      {
        heading: 'Pricing & Fees',
        body: 'There is a small one-time ETH fee to create a subscription plan on-chain. Subscription payments are made in SUBS tokens or USDC on the Arbitrum network, which means very low gas fees. Merchants set their own subscription pricing.',
      },
    ],
  },

  subscribe: {
    title: 'Subscribe',
    description: 'How subscribing works.',
    sections: [
      {
        heading: 'What is Subscribing?',
        body: 'Subscribing means paying a blockchain-based subscription fee to a merchant\'s plan. Once your payment is confirmed on-chain, the Telegram bot automatically grants you access to the group.',
      },
      {
        heading: 'How Payments Work',
        body: 'Payments are made on the Arbitrum network. You\'ll need a connected wallet (like MetaMask) with sufficient SUBS or USDC tokens. The transaction is executed directly with the Subscrypts smart contract \u2014 no intermediaries.',
      },
      {
        heading: 'USDC Fallback',
        body: 'If your SUBS token balance is too low to cover the subscription fee, the system can automatically use USDC to cover the shortfall. This swap happens on-chain via Uniswap V3 during the subscription transaction.',
      },
      {
        heading: 'Recurring Cycles',
        body: 'You can choose to subscribe for multiple cycles upfront. Each cycle extends your subscription by the plan\'s duration (e.g., 1 month). Subscribing for more cycles means fewer future transactions. You can also enable recurring payments to auto-renew.',
      },
      {
        heading: 'Referral Addresses',
        body: 'Some plans offer a referral bonus. If someone referred you, enter their wallet address in the referral field. Both you and the referrer may receive bonus subscription time, as configured by the merchant.',
      },
      {
        heading: 'After Subscribing',
        body: 'After subscribing, the Telegram bot checks your on-chain subscription status and sends you an invite link to the group. If your wallet is already linked to your Telegram account, access is granted automatically. Access is removed when your subscription expires.',
      },
      {
        heading: 'Why Subscribe URLs Change',
        body: 'Each subscribe URL contains a version number and a cryptographic signature. When a group admin updates subscription plans, a new version is generated with a fresh signature. This prevents anyone from tampering with the subscribe page \u2014 the data you see is guaranteed to match what the admin configured. If you visit an older link, you\'ll be automatically redirected to the latest version.',
      },
    ],
  },

  createSubscription: {
    title: 'Create Plan',
    description: 'How to create a subscription plan.',
    sections: [
      {
        heading: 'What is a Plan?',
        body: 'A plan is a subscription offer you create as a merchant. It defines the price, currency, duration, and other settings. Subscribers will see this plan and can subscribe to it. Each plan gets a unique on-chain ID.',
      },
      {
        heading: 'Pricing Model',
        body: 'Set the subscription amount in either SUBS or USDC tokens. The amount is per cycle \u2014 if your plan duration is 1 month and amount is 10 SUBS, subscribers pay 10 SUBS per month.',
      },
      {
        heading: 'Description & Attributes',
        body: 'The description and attribute fields are stored on-chain as bytes32 values (max 32 bytes / ~32 ASCII characters). Use the description for a human-readable plan name and attributes for metadata tags.',
      },
      {
        heading: 'Duration',
        body: 'Choose from presets (1 week, 1 month, 1 year) or set a custom duration. The duration defines one subscription cycle. Subscribers can purchase multiple cycles at once.',
      },
      {
        heading: 'Referral Bonus',
        body: 'Optionally set a referral bonus duration. When a subscriber is referred by another wallet, both parties can receive this bonus time added to their subscription. Set to 0 to disable referrals.',
      },
    ],
  },

  link: {
    title: 'Wallet Linking',
    description: 'How wallet linking works.',
    sections: [
      {
        heading: 'What is Wallet Linking?',
        body: 'Wallet linking connects your Ethereum wallet address to your Telegram account within a specific group. This allows the bot to verify your on-chain subscriptions and grant the appropriate group access.',
      },
      {
        heading: 'Why Sign a Message?',
        body: 'You\'ll be asked to sign a message (SIWE \u2014 Sign-In with Ethereum) to prove you own the wallet address. This is a free, gasless operation that doesn\'t send any transaction. It\'s a cryptographic proof of ownership.',
      },
      {
        heading: 'What Data is Stored',
        body: 'The bot stores the association between your Telegram user ID and your wallet address for the specific group. No private keys or sensitive wallet data are stored \u2014 only the public address and the signed verification.',
      },
      {
        heading: 'How to Unlink',
        body: 'You can unlink your wallet at any time by using the unlink command with the Telegram bot. This removes the association and the bot will no longer check your subscriptions for that group.',
      },
    ],
  },

  setup: {
    title: 'Merchant Setup',
    description: 'How to set up your Telegram group.',
    sections: [
      {
        heading: 'What is Merchant Setup?',
        body: 'Merchant setup connects your wallet to manage subscription plans for your Telegram group. As a merchant, you can create plans, set pricing, and manage subscriber access.',
      },
      {
        heading: 'Requirements',
        body: 'You need a crypto wallet (like MetaMask) connected to Arbitrum One. You\'ll also need a small amount of ETH for gas fees when creating plans.',
      },
      {
        heading: 'Next Steps',
        body: 'After setup, create subscription plans using the Create Plan page. Each plan defines a price, duration, and currency. Share the subscribe link with your Telegram group members so they can subscribe.',
      },
    ],
  },
};
